import { appBottomClearanceClass } from "@/components/common/appBottomClearance";
import ErrorPage from "@/components/pages/ErrorPage";
import { usersOrganisationsRolesQueryOptions, useSetRoles } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { userTabAccessQueryOptions } from "@/features/navigation/api/selectUserTabAccess";
import Navigation from "@/features/navigation/components/Navigation";
import SuperUserViewSwitch from "@/features/navigation/components/SuperUserViewSwitch";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { APP_SCROLL_CONTAINER_ID } from "@/services/scroll";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useKnockAuth } from "../features/knock/api/useKnockAuth";
import { getMfaState } from "@/features/auth/api/mfa";
import { getMfaRouteDecision, isMfaEnforced, isPrivilegedUser } from "@/features/auth/utils/mfaRouting";

function App() {
    useSetRoles();
    useKnockAuth();
    return (
        <main className="flex flex-col h-dvh overflow-hidden">
            <Navigation />
            <section
                id={APP_SCROLL_CONTAINER_ID}
                className={`flex flex-col flex-1 min-h-0 overflow-y-auto ${appBottomClearanceClass}`}
            >
                {/* Grow with the page. flex-1 would lock this box to the scrollport and long pages would scroll past the clearance. */}
                <div className="flex flex-col min-h-full shrink-0">
                    <Outlet />
                </div>
            </section>
            <SuperUserViewSwitch />
        </main>
    );
}

export const Route = createFileRoute("/_app")({
    component: App,
    beforeLoad: ({ context }) => {
        if (context?.auth?.isAuthenticated === false) {
            throw redirect({
                to: "/login",
            });
        }

        return context;
    },
    loader: async ({ context }) => {
        try {
            const roles = await context?.queryClient?.ensureQueryData(
                usersOrganisationsRolesQueryOptions(context?.auth?.id)
            );
            context?.auth?.roles?.setRoles(roles);
        } catch (error) {
            // If roles query fails, log error but don't block authentication
            // Users can still access the app even if role query fails temporarily
            console.error("Failed to load user roles:", error);
            context?.auth?.roles?.setRoles([]);
        }

        // Tabs drive the navigation. If this fails the nav shows Profile only, as with roles above.
        await context?.queryClient
            ?.ensureQueryData(userTabAccessQueryOptions(context?.auth?.id))
            .catch((error) => console.error("Failed to load tab access:", error));

        const profile = await context?.queryClient?.ensureQueryData(selectProfileByIdQueryOptions(context?.auth?.id));

        // If profile doesn't exist, redirect to onboarding
        if (!profile) {
            throw redirect({
                to: "/login",
            });
        }

        context?.auth?.setIsSuperUser(profile?.is_super_user ?? false);

        if (
            isMfaEnforced() &&
            isPrivilegedUser({
                isAdmin: Boolean(context.auth.roles?.isAdmin),
                isSuperUser: Boolean(profile?.is_super_user),
            })
        ) {
            const mfa = await getMfaState();
            const decision = getMfaRouteDecision({
                isPrivileged: true,
                currentLevel: mfa.currentLevel,
                nextLevel: mfa.nextLevel,
                hasVerifiedTotp: mfa.verifiedTotp.length > 0,
            });
            if (decision !== "continue") {
                const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                throw redirect({
                    to: decision === "enroll" ? "/mfa/enroll" : "/mfa/challenge",
                    search: { returnTo },
                });
            }
        }
    },
    errorComponent: ({ error, reset }) => <ErrorPage title="App Error" error={error as Error} reset={reset} />,
});
